/**
 * Enfeites das linhas.
 *
 * Duas ideias aqui:
 *
 * 1. Traço — como a linha é desenhada (cheia, tracejada, ondulada...).
 * 2. Motivo — um desenho que enfeita a linha: corações, patinhas, dentinhos,
 *    esmaltes... Ele pode aparecer repetido ao longo da linha (faixa de
 *    demarcação) ou na margem, no alto de cada linha (marcador de escrita).
 *
 * Tudo é resolvido em SVG embutido como `background-image`, sem medição em
 * tempo de execução: a impressão sai igual em qualquer navegador.
 */
import { limitar } from '../color/convert.js';
/** Espessura padrão do fio da pauta, em mm. */
export const ESPESSURA_LINHA_MM = 0.16;
/** Largura do ladrilho que desenha as linhas, em mm. */
const LARGURA_LADRILHO_MM = 12;
/** Largura da coluna do desenho quando ele fica na margem, em mm. */
const LARGURA_COLUNA_MARGEM_MM = 0.1;
/* ------------------------------------------------------------------ */
/* Glifos                                                              */
/* ------------------------------------------------------------------ */
/**
 * Desenhos em um quadrado de 24 × 24 unidades, apenas contorno (stroke), para
 * casar com o traço fino da pauta. `__COR__` é trocado pela cor escolhida.
 */
const GLIFOS = {
    NENHUM: '',
    CORACAO: '<path d="M12 20.2C7.4 16.9 3.4 13.7 3.4 9.7 3.4 7 5.4 5 7.8 5c1.6 0 3.1.9 4.2 2.5C13.1 5.9 14.6 5 16.2 5c2.4 0 4.4 2 4.4 4.7 0 4-4 7.2-8.6 10.5Z"/>',
    PATINHA: '<ellipse cx="12" cy="16.6" rx="4.4" ry="3.4"/><ellipse cx="5.7" cy="11.6" rx="1.8" ry="2.3"/><ellipse cx="9.7" cy="7.6" rx="2" ry="2.6"/><ellipse cx="14.3" cy="7.6" rx="2" ry="2.6"/><ellipse cx="18.3" cy="11.6" rx="1.8" ry="2.3"/>',
    ESTRELA: '<path d="M12 4.2l2.34 4.74 5.23.76-3.79 3.69.9 5.21L12 16.14l-4.68 2.46.9-5.21L4.43 9.7l5.23-.76Z"/>',
    FLOR: '<ellipse cx="12" cy="6.8" rx="2.2" ry="3"/><ellipse cx="12" cy="17.2" rx="2.2" ry="3"/><ellipse cx="6.8" cy="12" rx="3" ry="2.2"/><ellipse cx="17.2" cy="12" rx="3" ry="2.2"/><circle cx="12" cy="12" r="1.5"/>',
    LACO: '<path d="M11.1 12 4.3 8.1v7.8Z"/><path d="M12.9 12l6.8-3.9v7.8Z"/><circle cx="12" cy="12" r="1.3"/>',
    FOLHA: '<path d="M5.4 18.6C9 18.6 18.6 15.6 18.6 5.4 8.4 5.4 5.4 15 5.4 18.6Z"/><path d="M5.4 18.6 18.6 5.4"/>',
    GOTA: '<path d="M12 4.2c3.9 4.6 5.9 7.6 5.9 10.1A5.9 5.9 0 0 1 6.1 14.3c0-2.5 2-5.5 5.9-10.1Z"/>',
    LIVRO: '<path d="M12 6.6C10.4 5.3 8.2 4.9 4.6 5.1v12.4c3.6-.2 5.8.2 7.4 1.5 1.6-1.3 3.8-1.7 7.4-1.5V5.1c-3.6-.2-5.8.2-7.4 1.5Z"/><path d="M12 6.6v12.4"/>',
    LAPIS: '<path d="M4.4 19.6l1-4 9.6-9.6 3 3-9.6 9.6Z"/><path d="M13.6 7.4l3 3"/><path d="M5.4 15.6l3 3"/>',
    MALETA: '<path d="M9.4 7.2V5.8a1.4 1.4 0 0 1 1.4-1.4h2.4a1.4 1.4 0 0 1 1.4 1.4v1.4"/><path d="M4.6 7.2h14.8a1.2 1.2 0 0 1 1.2 1.2v9.4a1.2 1.2 0 0 1-1.2 1.2H4.6a1.2 1.2 0 0 1-1.2-1.2V8.4a1.2 1.2 0 0 1 1.2-1.2Z"/><path d="M3.4 12.2h17.2"/>',
    GRAFICO: '<path d="M4 19.6h16"/><path d="M7.2 19.6v-6.2"/><path d="M12 19.6V6.8"/><path d="M16.8 19.6v-8.8"/>',
    PRANCHETA: '<path d="M6.6 5.4h10.8a1.4 1.4 0 0 1 1.4 1.4v12.2H5.2V6.8a1.4 1.4 0 0 1 1.4-1.4Z"/><path d="M9.4 4.4h5.2v2.8H9.4Z"/><path d="M8.4 12h7.2"/><path d="M8.4 15.4h4.6"/>',
    BALANCA: '<path d="M12 4.6v14.8"/><path d="M6.8 19.4h10.4"/><path d="M3.6 8.4h16.8"/><path d="M3.6 8.4 6.4 13.8H0.8Z"/><path d="M20.4 8.4l2.8 5.4h-5.6Z"/><path d="M20.4 8.4 17.6 13.8H23.2Z"/>',
    NOTA: '<path d="M10 18V6.2l8-1.6v11.6"/><ellipse cx="7.8" cy="18.2" rx="2.6" ry="2.1"/><ellipse cx="15.8" cy="16.6" rx="2.6" ry="2.1"/>',
    PINCEL: '<path d="M15.6 4.4l4 4-7.2 7.2-4-4Z"/><path d="M8.4 11.6 4.6 19.4l7.8-3.8"/>',
    XICARA: '<path d="M4.6 8.6h11.2v6.6a4.2 4.2 0 0 1-4.2 4.2H8.8a4.2 4.2 0 0 1-4.2-4.2Z"/><path d="M15.8 10.6h1.6a2.6 2.6 0 0 1 0 5.2h-1.6"/><path d="M7.6 5.6c0-1 .8-1.6 1.6-1.6"/>',
    CUPCAKE: '<path d="M5.6 12.4h12.8l-1.3 6.2a1.4 1.4 0 0 1-1.4 1.1H8.3a1.4 1.4 0 0 1-1.4-1.1Z"/><path d="M6.4 12.4C5.1 10.8 6 8.5 8 8.2c.2-1.9 2-3.1 3.7-2.5.9-1.5 3.1-1.5 4 0 1.7-.6 3.5.6 3.7 2.5 2 .3 2.9 2.6 1.6 4.2Z"/>',
    TESOURA: '<path d="M8.6 4.2 15.2 15.6"/><path d="M15.4 4.2 8.8 15.6"/><circle cx="6.8" cy="18.4" r="2.2"/><circle cx="17.2" cy="18.4" r="2.2"/>',
    ESMALTE: '<path d="M10.2 3.4h3.6v3.4h-3.6Z"/><path d="M8.8 6.8h6.4v3.2H8.8Z"/><path d="M8 10h8a1.2 1.2 0 0 1 1.2 1.2v9.4H6.8v-9.4A1.2 1.2 0 0 1 8 10Z"/>',
    DENTE: '<path d="M7 5.6c1.6-1 3.2-1.1 5-.5 1.8-.6 3.4-.5 5 .5 2 1.2 2.2 3.6 1.6 6.3-.5 2.2-.9 4-1.2 5.6-.2 1.3-.7 1.9-1.4 1.9-.8 0-1.2-.6-1.5-2-.3-1.4-.6-2.6-1.4-2.6s-1.1 1.2-1.4 2.6c-.3 1.4-.7 2-1.5 2-.7 0-1.2-.6-1.4-1.9-.3-1.6-.7-3.4-1.2-5.6C4.8 9.2 5 6.8 7 5.6Z"/>',
    CRUZ_MEDICA: '<path d="M9.6 4.5h4.8v5.1h5.1v4.8h-5.1v5.1H9.6v-5.1H4.5V9.6h5.1Z"/>',
    PULSO: '<path d="M2.6 12.6h3.6l1.8-5.4 2.6 10.6 2.4-7.6 1.4 2.4h7"/>',
    ASPAS: '<path d="M9.4 6.6C6.4 7.6 4.6 10 4.6 13c0 2.6 1.5 4.4 3.7 4.4 1.9 0 3.3-1.4 3.3-3.3 0-1.8-1.2-3.1-3-3.1-.3 0-.6 0-.8.1.4-1.5 1.6-2.7 3.2-3.4Z"/><path d="M18.6 6.6c-3 1-4.8 3.4-4.8 6.4 0 2.6 1.5 4.4 3.7 4.4 1.9 0 3.3-1.4 3.3-3.3 0-1.8-1.2-3.1-3-3.1-.3 0-.6 0-.8.1.4-1.5 1.6-2.7 3.2-3.4Z"/>',
    ALVO: '<circle cx="12" cy="12" r="8.2"/><circle cx="12" cy="12" r="4.4"/><circle cx="12" cy="12" r="1"/>',
    LUA: '<path d="M19.6 15.4A8.4 8.4 0 0 1 8.6 4.4a8.6 8.6 0 1 0 11 11Z"/>',
    CRUZ: '<path d="M12 4.2v15.6"/><path d="M6.8 9.2h10.4"/>',
    CHECK: '<circle cx="12" cy="12" r="8.4"/><path d="M8.2 12.4l2.7 2.8 5-5.8"/>',
    TELEFONE: '<path d="M7.4 4.6h2.4l1.4 3.6-2 1.4a10.6 10.6 0 0 0 5.2 5.2l1.4-2 3.6 1.4v2.4a2 2 0 0 1-2.2 2A15.4 15.4 0 0 1 5.4 6.8a2 2 0 0 1 2-2.2Z"/>',
    LAMPADA: '<path d="M12 3.6a5.6 5.6 0 0 0-3.3 10.1v2.1h6.6v-2.1A5.6 5.6 0 0 0 12 3.6Z"/><path d="M9.4 18.4h5.2"/><path d="M10.2 20.6h3.6"/>',
    CARINHA: '<circle cx="12" cy="12" r="8.4"/><path d="M8.6 14.4a4.4 4.4 0 0 0 6.8 0"/><path d="M9.4 9.6h.01"/><path d="M14.6 9.6h.01"/>',
    SOL: '<circle cx="12" cy="12" r="4.2"/><path d="M12 3.4v2.4"/><path d="M12 18.2v2.4"/><path d="M3.4 12h2.4"/><path d="M18.2 12h2.4"/><path d="M6 6l1.7 1.7"/><path d="M16.3 16.3 18 18"/><path d="M18 6l-1.7 1.7"/><path d="M7.7 16.3 6 18"/>',
    MOEDA: '<circle cx="12" cy="12" r="8.4"/><path d="M12 7.4v9.2"/><path d="M14.8 9.6a3 3 0 0 0-2.8-1.2c-1.5 0-2.6.8-2.6 2 0 2.6 5.4 1.3 5.4 3.9 0 1.2-1.1 2-2.8 2a3.2 3.2 0 0 1-3-1.4"/>',
    RELOGIO: '<circle cx="12" cy="12" r="8.4"/><path d="M12 7v5.2l3.4 2.2"/>',
    CALENDARIO: '<path d="M5.4 6.4h13.2a1.2 1.2 0 0 1 1.2 1.2v10.2a1.2 1.2 0 0 1-1.2 1.2H5.4a1.2 1.2 0 0 1-1.2-1.2V7.6a1.2 1.2 0 0 1 1.2-1.2Z"/><path d="M4.2 10.4h15.6"/><path d="M8.6 4.4v3.6"/><path d="M15.4 4.4v3.6"/>',
    INTERROGACAO: '<path d="M9.2 8.6A2.9 2.9 0 0 1 12 5.6c1.7 0 3 1.1 3 2.7 0 2.4-3 2.3-3 4.6"/><path d="M12 18.4h.01"/>',
    OLHO: '<path d="M2.6 12S6 6.6 12 6.6 21.4 12 21.4 12 18 17.4 12 17.4 2.6 12 2.6 12Z"/><circle cx="12" cy="12" r="2.8"/>',
};
/** Markup interno do desenho (sem a tag `<svg>`), com `__COR__` no lugar da cor. */
export function glifoDoMotivo(motivo) {
    return GLIFOS[motivo] ?? '';
}
/* ------------------------------------------------------------------ */
/* Utilidades de SVG                                                   */
/* ------------------------------------------------------------------ */
function numero(valor) {
    return String(Number(valor.toFixed(3)));
}
function urlDeSvg(conteudo, larguraMm, alturaMm, alinhamento = 'xMinYMin') {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${numero(larguraMm)}mm" height="${numero(alturaMm)}mm" ` +
        `viewBox="0 0 ${numero(larguraMm)} ${numero(alturaMm)}" preserveAspectRatio="${alinhamento}" ` +
        `fill="none" stroke-linecap="round" stroke-linejoin="round">${conteudo}</svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
/** Grupo do desenho posicionado em mm, com traço constante independente do tamanho. */
export function grupoDoMotivo(motivo, cor, xMm, yMm, tamanhoMm) {
    const glifo = glifoDoMotivo(motivo);
    if (!glifo)
        return '';
    const escala = tamanhoMm / 24;
    const traco = (tamanhoMm * 0.1) / escala;
    return (`<g transform="translate(${numero(xMm)} ${numero(yMm)}) scale(${numero(escala)})" ` +
        `stroke="${cor}" stroke-width="${numero(traco)}">${glifo.replace(/__COR__/g, cor)}</g>`);
}
/* ------------------------------------------------------------------ */
/* Traço                                                              */
/* ------------------------------------------------------------------ */
/**
 * Traço da linha já com a cor aplicada.
 *
 * A cor precisa estar declarada: o SVG que envolve este conteúdo usa
 * `fill="none"` e, sem `stroke`, o padrão do SVG é `stroke: none` — o traço
 * simplesmente não desenha e a área de escrita sai em branco.
 */
function conteudoTraco(traco, espessuraMm, larguraMm, yMm, cor) {
    return `<g stroke="${cor}">${desenhoDoTraco(traco, espessuraMm, larguraMm, yMm, cor)}</g>`;
}
function desenhoDoTraco(traco, espessuraMm, larguraMm, yMm, cor) {
    const ondulada = () => {
        const passo = larguraMm / 4;
        const amp = Math.max(0.35, espessuraMm * 3);
        let caminho = `M0 ${numero(yMm)}`;
        for (let i = 0; i < 4; i += 1) {
            const direcao = i % 2 === 0 ? -1 : 1;
            caminho += ` q ${numero(passo / 2)} ${numero(amp * direcao)} ${numero(passo)} 0`;
        }
        return `<path d="${caminho}"/>`;
    };
    const zigue = () => {
        const passo = larguraMm / 8;
        const amp = Math.max(0.4, espessuraMm * 3.2);
        let caminho = `M0 ${numero(yMm)}`;
        for (let i = 0; i < 8; i += 1) {
            caminho += ` l ${numero(passo / 2)} ${numero(-amp)} l ${numero(passo / 2)} ${numero(amp)}`;
        }
        return `<path d="${caminho}"/>`;
    };
    const linha = (deslocamentoMm, espessura = espessuraMm) => `<line x1="0" y1="${numero(yMm - deslocamentoMm)}" x2="${numero(larguraMm)}" y2="${numero(yMm - deslocamentoMm)}" stroke-width="${numero(espessura)}"/>`;
    switch (traco) {
        case 'TRACEJADA':
            return `<line x1="0" y1="${numero(yMm)}" x2="${numero(larguraMm)}" y2="${numero(yMm)}" stroke-width="${numero(espessuraMm)}" stroke-dasharray="2.4 1.5"/>`;
        case 'PONTILHADA':
            return `<line x1="0" y1="${numero(yMm)}" x2="${numero(larguraMm)}" y2="${numero(yMm)}" stroke-width="${numero(Math.max(0.1, espessuraMm))}" stroke-dasharray="0.2 1.2"/>`;
        case 'DUPLA':
            return `<g stroke-width="${numero(Math.max(0.1, espessuraMm * 0.7))}">${linha(0)}${linha(Math.max(0.6, espessuraMm * 3.6))}</g>`;
        case 'TRIPLA': {
            const vao = Math.max(0.5, espessuraMm * 3);
            return `<g stroke-width="${numero(Math.max(0.09, espessuraMm * 0.6))}">${linha(0)}${linha(vao)}${linha(vao * 2)}</g>`;
        }
        case 'ONDULADA':
            return `<g stroke-width="${numero(espessuraMm)}">${ondulada()}</g>`;
        case 'ZIGUE':
            return `<g stroke-width="${numero(espessuraMm)}">${zigue()}</g>`;
        case 'DEGRADE':
            return (`<defs><linearGradient id="d" x1="0" y1="0" x2="1" y2="0">` +
                `<stop offset="0" stop-color="${cor}" stop-opacity="0.15"/>` +
                `<stop offset="0.5" stop-color="${cor}" stop-opacity="1"/>` +
                `<stop offset="1" stop-color="${cor}" stop-opacity="0.15"/>` +
                `</linearGradient></defs>` +
                `<line x1="0" y1="${numero(yMm)}" x2="${numero(larguraMm)}" y2="${numero(yMm)}" stroke-width="${numero(espessuraMm)}" stroke="url(#d)"/>`);
        case 'SOLIDA':
        default:
            return linha(0);
    }
}
/** Altura da linha em mm, aceitando as duas formas usadas no projeto. */
export function alturaDaPauta(entrada) {
    return entrada.alturaLinhaMm ?? entrada.alturaLinha ?? 6;
}
/** Espaço extra entre linhas, aceitando as duas formas usadas no projeto. */
export function espacamentoDaPauta(entrada) {
    return entrada.espacamentoMm ?? entrada.espacamento ?? 0;
}
export function pautaEfetiva(entrada) {
    const tamanho = limitar(entrada.motivoTamanhoMm ?? 3.2, 1.6, 8);
    return {
        estilo: entrada.estilo,
        traco: entrada.traco ?? (entrada.estilo === 'PONTILHADO' ? 'PONTILHADA' : 'SOLIDA'),
        motivo: entrada.motivo ?? 'NENHUM',
        motivoModo: entrada.motivoModo ?? 'AO_LONGO_DA_LINHA',
        motivoACadaNLinhas: Math.round(limitar(entrada.motivoACadaNLinhas ?? 1, 1, 4)),
        motivoEspacamentoMm: limitar(entrada.motivoEspacamentoMm ?? 18, tamanho + 2.5, 60),
        motivoTamanhoMm: tamanho,
        motivoLado: entrada.motivoLado ?? 'ESQUERDA',
        corMotivo: entrada.corMotivo ?? null,
    };
}
/** Props de `BlocoLinhas` que carregam os enfeites escolhidos. */
export function enfeitesDePauta(escrita) {
    return {
        traco: escrita.traco ?? (escrita.estilo === 'PONTILHADO' ? 'PONTILHADA' : 'SOLIDA'),
        motivo: escrita.motivo ?? 'NENHUM',
        motivoModo: escrita.motivoModo ?? 'AO_LONGO_DA_LINHA',
        motivoACadaNLinhas: escrita.motivoACadaNLinhas ?? 1,
        motivoEspacamentoMm: escrita.motivoEspacamentoMm ?? 18,
        motivoTamanhoMm: escrita.motivoTamanhoMm ?? 3.2,
        motivoLado: escrita.motivoLado ?? 'ESQUERDA',
        corMotivo: escrita.corMotivo ?? null,
    };
}
/** Separador de seção que acompanha o desenho escolhido para as linhas. */
export function separadorEnfeitado(escrita, opcoes = {}) {
    const motivo = escrita.motivo ?? 'NENHUM';
    return {
        estilo: motivo === 'NENHUM' ? 'LINHA' : 'MOTIVO',
        motivo,
        motivoTamanhoMm: escrita.motivoTamanhoMm ?? 3.2,
        corMotivo: escrita.corMotivo ?? null,
        cor: opcoes.cor ?? 'DESTAQUE',
        espessuraMm: opcoes.espessuraMm ?? 0.4,
        espacoMm: opcoes.espacoMm ?? 3,
    };
}
function camadaDeMotivos(p, passoMm, cores) {
    if (p.motivo === 'NENHUM')
        return [];
    const cor = p.corMotivo ?? cores.destaque;
    const tamanho = p.motivoTamanhoMm;
    // Faixa de demarcação: o desenho se repete de espaço em espaço sobre a linha.
    if (p.motivoModo === 'AO_LONGO_DA_LINHA') {
        const largura = Math.max(p.motivoEspacamentoMm, tamanho + 3);
        const x = (largura - tamanho) / 2;
        const base = passoMm - ESPESSURA_LINHA_MM * 1.9;
        const y = Math.max(0.2, base - tamanho);
        return [
            {
                imagem: urlDeSvg(grupoDoMotivo(p.motivo, cor, x, y, tamanho), largura, passoMm),
                tamanho: `${numero(largura)}mm ${numero(passoMm)}mm`,
                repeticao: 'repeat',
                posicao: `${numero(largura / 2)}mm 0`,
            },
        ];
    }
    // Marcador de escrita: um desenho na margem, a cada N linhas.
    const aCadaN = Math.max(1, p.motivoACadaNLinhas);
    const altura = passoMm * aCadaN;
    const larguraLadrilho = tamanho + Math.max(0.4, LARGURA_COLUNA_MARGEM_MM);
    const topo = Math.max(0.1, (passoMm - ESPESSURA_LINHA_MM * 1.9 - tamanho) / 2);
    const desenhar = (lado) => {
        const x = lado === 'DIREITA' ? larguraLadrilho - tamanho : 0;
        return {
            imagem: urlDeSvg(grupoDoMotivo(p.motivo, cor, x, topo, tamanho), larguraLadrilho, altura, lado === 'DIREITA' ? 'xMaxYMin' : 'xMinYMin'),
            tamanho: `${numero(larguraLadrilho)}mm ${numero(altura)}mm`,
            repeticao: 'repeat-y',
            posicao: lado === 'DIREITA' ? 'right 0' : 'left 0',
        };
    };
    if (p.motivoLado === 'AMBOS')
        return [desenhar('ESQUERDA'), desenhar('DIREITA')];
    return [desenhar(p.motivoLado)];
}
/**
 * CSS da pauta pronta para o React: as linhas viram `background-image` em SVG,
 * com os desenhos por cima.
 */
export function cssDaPauta(entrada, cores) {
    const p = pautaEfetiva(entrada);
    const passoMm = Math.max(2.5, alturaDaPauta(entrada) + espacamentoDaPauta(entrada));
    const camadas = [];
    if (p.estilo === 'PONTOS') {
        camadas.push({
            imagem: `radial-gradient(circle, ${cores.linha} 0 0.16mm, transparent 0.16mm)`,
            tamanho: `${numero(passoMm)}mm ${numero(passoMm)}mm`,
            repeticao: 'repeat',
            posicao: '0 0',
        });
    }
    else if (p.estilo === 'QUADRICULADO') {
        camadas.push({
            imagem: `repeating-linear-gradient(to bottom, transparent 0, transparent ${numero(passoMm - ESPESSURA_LINHA_MM)}mm, ${cores.linha} ${numero(passoMm - ESPESSURA_LINHA_MM)}mm, ${cores.linha} ${numero(passoMm)}mm), ` +
                `repeating-linear-gradient(to right, transparent 0, transparent ${numero(passoMm - ESPESSURA_LINHA_MM)}mm, ${cores.linha} ${numero(passoMm - ESPESSURA_LINHA_MM)}mm, ${cores.linha} ${numero(passoMm)}mm)`,
            tamanho: 'auto auto',
            repeticao: 'repeat',
            posicao: '0 0',
        });
    }
    else if (p.estilo !== 'SEM_LINHA') {
        /**
         * O fio é desenhado na base do ladrilho, mas com o centro recuado meia
         * espessura: em `y = passoMm` o traço cairia exatamente na borda e metade
         * dele seria cortada pelo recorte do SVG, virando um fio quase invisível.
         */
        camadas.push({
            imagem: urlDeSvg(conteudoTraco(p.traco, ESPESSURA_LINHA_MM, LARGURA_LADRILHO_MM, passoMm - ESPESSURA_LINHA_MM / 2, cores.linha), LARGURA_LADRILHO_MM, passoMm),
            tamanho: `${numero(LARGURA_LADRILHO_MM)}mm ${numero(passoMm)}mm`,
            repeticao: 'repeat',
            posicao: '0 0',
        });
    }
    // Os desenhos entram na frente das linhas.
    camadas.unshift(...camadaDeMotivos(p, passoMm, cores));
    if (camadas.length === 0)
        return {};
    return {
        backgroundImage: camadas.map((camada) => camada.imagem).join(', '),
        backgroundSize: camadas.map((camada) => camada.tamanho).join(', '),
        backgroundRepeat: camadas.map((camada) => camada.repeticao).join(', '),
        backgroundPosition: camadas.map((camada) => camada.posicao).join(', '),
    };
}
//# sourceMappingURL=motivos.js.map